import { Loader2 } from 'lucide-react'
import type { TenantPublico } from '@/lib/sesion'
import { api } from '@/lib/api'

interface Props {
  tenant: TenantPublico | null
  cargando: boolean
}

// Colores de las categorías del rubro. Es un guiño, y sin proponérselo la
// misma paleta que después ordena la grilla del mostrador.
const FRANJA = ['#D9822B', '#2F7A4F', '#8A6D3B', '#3B6EA8', '#6B4E9B', '#C4A02C']

// Panel de marca del login.
//
// No es decoración: es lo que le confirma al empleado que está entrando al
// sistema de SU local y no al de otro. Por eso el nombre del local es el
// elemento más grande de la pantalla, más que el nombre del producto.
//
// El fondo se construye con gradientes y un patrón de puntos en lugar de una
// foto: una imagen de verduras pesaría cientos de KB, y esta pantalla la carga
// una caja con la persiana recién abierta y la conexión del local.
//
// Hay dos versiones y no una sola adaptada con clases: en el celular el panel
// tiene que ser una banda finita ARRIBA del formulario. Si se apila el panel
// grande, el nombre del local queda debajo del teclado virtual y el empleado
// no ve a qué local está entrando, que es justo lo único que este panel tiene
// que comunicar.
export function PanelMarca({ tenant, cargando }: Props) {
  const logo = api.urlImagen(tenant?.logoUrl)

  return (
    <>
      <BandaMovil tenant={tenant} cargando={cargando} logo={logo} />
      <PanelEscritorio tenant={tenant} cargando={cargando} logo={logo} />
    </>
  )
}

interface PropsInternas extends Props {
  logo: string | undefined
}

function BandaMovil({ tenant, cargando, logo }: PropsInternas) {
  return (
    <header className="relative overflow-hidden bg-tinta px-5 pb-5 pt-6 text-crema lg:hidden">
      <Fondo />

      <div className="relative flex items-center gap-3.5">
        {cargando ? (
          <Loader2 className="h-9 w-9 animate-spin text-crema/40" />
        ) : logo ? (
          <img
            src={logo}
            alt=""
            className="h-11 w-11 shrink-0 rounded-lg bg-crema/5 object-contain p-1"
            loading="eager"
          />
        ) : (
          <div
            className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-verde
                       text-[16px] font-bold text-white"
            aria-hidden
          >
            {tenant ? iniciales(tenant.nombre) : 'F'}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[19px] font-bold leading-tight tracking-[-0.02em]">
            {cargando ? 'Buscando el local…' : (tenant?.nombre ?? 'Fruver')}
          </h2>
          <p className="mt-0.5 text-[12.5px] text-crema/50">
            {tenant ? 'Caja, precios y control' : 'El sistema de tu verdulería'}
          </p>
        </div>
      </div>

      {/* La franja va abajo del todo, como filete que separa la banda del
          formulario. En dos píxeles alcanza para que se lea como marca. */}
      <div className="absolute inset-x-0 bottom-0 flex" aria-hidden>
        {FRANJA.map((c) => (
          <span key={c} className="h-[3px] flex-1" style={{ background: c }} />
        ))}
      </div>
    </header>
  )
}

function PanelEscritorio({ tenant, cargando, logo }: PropsInternas) {
  return (
    <aside
      className="relative hidden shrink-0 flex-col justify-between overflow-hidden
                 bg-tinta px-12 py-14 text-crema
                 lg:flex lg:w-[46%] lg:max-w-[560px]"
    >
      <Fondo />

      {/* Marca del producto, discreta: el protagonista es el local. */}
      <p className="relative text-[15px] font-bold tracking-[-0.01em]">
        Fruver<span className="text-verde-claro">.</span>
      </p>

      <div className="relative">
        {cargando ? (
          <div className="flex items-center gap-2.5 text-crema/60">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-[14px]">Buscando el local…</span>
          </div>
        ) : tenant ? (
          <>
            {logo ? (
              <img
                src={logo}
                alt=""
                // Contenido, no cover: el logo de una verdulería suele ser un
                // isotipo con márgenes propios y recortarlo lo arruina.
                className="mb-6 h-20 w-auto max-w-[220px] object-contain"
                loading="eager"
              />
            ) : (
              <div
                className="mb-6 grid h-20 w-20 place-items-center rounded-xl bg-verde
                           text-[32px] font-bold text-white"
                aria-hidden
              >
                {iniciales(tenant.nombre)}
              </div>
            )}

            <h2 className="max-w-[13ch] text-[46px] font-bold leading-[1.05] tracking-[-0.03em]">
              {tenant.nombre}
            </h2>

            <p className="mt-4 max-w-[34ch] text-[14.5px] leading-relaxed text-crema/55">
              Caja, precios y control de la verdulería.
            </p>
          </>
        ) : (
          <>
            <h2 className="max-w-[15ch] text-[46px] font-bold leading-[1.05] tracking-[-0.03em]">
              El sistema de tu verdulería
            </h2>
            <p className="mt-4 max-w-[34ch] text-[14.5px] leading-relaxed text-crema/55">
              Caja, precios y control, con o sin internet.
            </p>
          </>
        )}
      </div>

      <div className="relative">
        <div className="flex gap-1.5" aria-hidden>
          {FRANJA.map((c) => (
            <span key={c} className="h-1 flex-1 rounded-full" style={{ background: c }} />
          ))}
        </div>
        <p className="mt-4 text-[12px] text-crema/35">
          Fruta · Hoja · Hortaliza · Envasados · Almacén · Huevos
        </p>
      </div>
    </aside>
  )
}

// Fondo del panel: un resplandor verde arriba a la izquierda, uno dorado abajo
// a la derecha y un patrón de puntos muy tenue. Da profundidad sin pesar nada
// y sin distraer.
function Fondo() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-[380px] w-[380px] rounded-full opacity-[0.14]"
        style={{ background: 'radial-gradient(circle, #7FBF97 0%, transparent 68%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -right-16 h-[300px] w-[300px] rounded-full opacity-[0.08]"
        style={{ background: 'radial-gradient(circle, #C4A02C 0%, transparent 70%)' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.045]"
        style={{
          backgroundImage: 'radial-gradient(#F6F4EF 1px, transparent 1px)',
          backgroundSize: '22px 22px',
        }}
      />
    </>
  )
}

function iniciales(nombre: string): string {
  const partes = nombre.split(' ').filter(Boolean)
  if (partes.length === 0) return '?'
  if (partes.length === 1) return partes[0]!.slice(0, 2).toUpperCase()
  return `${partes[0]![0]}${partes[partes.length - 1]![0]}`.toUpperCase()
}
